import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { I18n } from 'react-redux-i18n-lite';
import { BottomSheet, BottomSheetAction, Label, MoreOptionsButton } from '@netexknowledge/smart-components';
import VideocallTopButton from '../../classroom/videocall/VideocallTopButton';
import ClassCodeBlock from '../sheets/ClassCodeBlock';

/**
 * Mobile ⋮ of the class header (Vista Mobile 1d). The header row only keeps the essentials, so the class
 * code, the searcher and the videocall move into this sheet; each one only when it is enabled.
 */
const ClassMoreSheet = ({
  classroomName,
  classCode,
  roleChipLabel,
  showClassCode,
  showSearch,
  showVideocall,
  videocallProps,
  onSearch,
  onCodeCopied,
  onOpen
}) => {
  const [open, setOpen] = useState(false);
  const videocallRef = useRef(null);

  // VideocallTopButton keeps the meeting logic (permissions, create or join); the row forwards the tap to it
  const startVideocall = event => {
    const icon = videocallRef.current && videocallRef.current.querySelector('img');
    if (icon && event.target !== icon) {
      icon.click();
    }
  };

  return (
    <>
      <MoreOptionsButton
        color="gray"
        active={open}
        aria-label={I18n.t('mobileSheets.moreOptions')}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={event => {
          event.currentTarget.blur();
          setOpen(true);
          if (onOpen) onOpen();
        }}
      />
      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        closeLabel={I18n.t('common.close')}
        overline={I18n.t('mobileSheets.classroom')}
        title={
          <span className="class-sheet-title">
            <span>{classroomName}</span>
            {roleChipLabel && <Label text={` ${roleChipLabel}`} type={'filled'} />}
          </span>
        }
      >
        {showClassCode && <ClassCodeBlock classCode={classCode} onCodeCopied={onCodeCopied} />}
        {showSearch && (
          <BottomSheetAction
            icon={<i className="custom-icons-magnifying-glass-browser" />}
            label={I18n.t('mobileSheets.searchInClassroom')}
            onClick={() => {
              setOpen(false);
              onSearch();
            }}
          />
        )}
        {showVideocall && (
          <div
            ref={videocallRef}
            className="class-sheet-videocall"
            role="button"
            tabIndex={0}
            onClick={startVideocall}
            onKeyDown={event => event.key === 'Enter' && startVideocall(event)}
          >
            <VideocallTopButton {...videocallProps} />
            <span>{I18n.t('mobileSheets.startVideocall')}</span>
          </div>
        )}
      </BottomSheet>
    </>
  );
};

ClassMoreSheet.propTypes = {
  classroomName: PropTypes.string,
  classCode: PropTypes.string,
  roleChipLabel: PropTypes.string,
  showClassCode: PropTypes.bool,
  showSearch: PropTypes.bool,
  showVideocall: PropTypes.bool,
  videocallProps: PropTypes.object,
  onSearch: PropTypes.func.isRequired,
  onCodeCopied: PropTypes.func,
  onOpen: PropTypes.func
};

export default ClassMoreSheet;
